import { KrCard } from '@/common/card/KrCard';
import { KrChip } from '@/common/card/KrChip';
import { useFieldDefs } from '@/common/entityFramework/entityForm/FieldDefsContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useForm } from '../FormContext';

const isEndOfTime = (instant) => {
  if (!instant) return false;
  const d = dayjs(instant);
  return d.isValid() && d.year() >= 9999;
};

const formatInstant = (instant) => {
  if (!instant) return '—';
  if (isEndOfTime(instant)) return '—';
  const formatted = StringUtils.toLocalInstant(instant);
  return formatted || '—';
};

const Row = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
    <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.02em' }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Stack>
);

const Value = ({ children }) => {
  children = children || '—';

  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </Typography>
  );
};

const SectionHeader = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

// Small badge with squarer corners — visually consistent with the StatusFormCard chip
const Badge = ({ label, color }) => <KrChip label={label} color={color} />;

const SrcSysValue = ({ value, srcSysFieldDef }) => {
  if (!value) return <Value>—</Value>;
  const enumSpec = srcSysFieldDef?.values?.find((v) => v.code === value);
  return <Value>{enumSpec?.displayName ?? value}</Value>;
};

const SorValue = ({ value }) => {
  if (value === true) return <Badge label="Yes" color="success" />;
  if (value === false) return <Badge label="No" />;
  return <Value></Value>;
};

const EndedValue = ({ instant }) => {
  if (isEndOfTime(instant)) return <Badge label="Current" />;
  if (!instant) return <Value>—</Value>;
  return <Value>{formatInstant(instant)}</Value>;
};

export const EntityAuditCard = () => {
  const { fieldDefs } = useFieldDefs();
  const { formValues } = useForm();
  const v = formValues ?? {};

  const srcSysFieldDef = useMemo(() => fieldDefs?.find((fieldDef) => fieldDef.field === 'srcSys'), [fieldDefs]);

  return (
    <KrCard minHeight={0} title="Audit" icon={fas.faShieldCheck} variant="soft">
      <Stack direction="column" gap={1.25}>
        {/* Provenance — no header, comes first */}
        <Row label="Recorded By">
          <Value>{v.recBy}</Value>
        </Row>
        <Row label="Source System">
          <SrcSysValue value={v.srcSys} srcSysFieldDef={srcSysFieldDef} />
        </Row>
        <Row label="System of Record">
          <SorValue value={v.sor} />
        </Row>

        <SectionHeader>Lifecycle</SectionHeader>
        <Row label="Version">
          <Value>{v.version}</Value>
        </Row>
        <Row label="Started">
          <Value>{formatInstant(v.verStartInstant)}</Value>
        </Row>
        <Row label="Ended">
          <EndedValue instant={v.verEndInstant} />
        </Row>

        <SectionHeader>Identifiers</SectionHeader>
        <Row label="ID">
          <Value>{v.id}</Value>
        </Row>
        <Row label="Reference">
          <Value>{v.ref}</Value>
        </Row>
      </Stack>
    </KrCard>
  );
};
